import { useState } from 'react';
import { ArrowUp, ArrowDown, GripVertical, Save } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';

export interface ReorderItem {
  id: string;
  name: string;
}

interface ReorderListDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  items: ReorderItem[];
  /** Persist the new order — receives ids in their new order (index = sort_order). */
  onSave: (orderedIds: string[]) => Promise<void>;
  saving?: boolean;
}

/**
 * Reusable "set the POS display order" dialog — mirrors the Favorites reorder
 * UX (native drag + up/down arrows, position = sort_order). Used for
 * departments, categories, and per-category product ordering. The parent
 * persists each item's sort_order = its index on save.
 */
export function ReorderListDialog({ open, onClose, title, items, onSave, saving = false }: ReorderListDialogProps) {
  const [draft, setDraft] = useState<ReorderItem[]>(items);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Re-seed the draft whenever the dialog transitions to open — done during
  // render (React's reset-state-on-prop-change pattern) rather than in an
  // effect, which would flag a setState-in-effect cascade.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(items);
  }

  function move(index: number, dir: -1 | 1) {
    setDraft((d) => {
      const next = d.slice();
      const j = index + dir;
      if (j < 0 || j >= next.length) return d;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }

  function reorder(from: number, to: number) {
    setDraft((d) => {
      if (from === to || from < 0 || to < 0 || from >= d.length || to >= d.length) return d;
      const next = d.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      widthClass="max-w-[560px]"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => onSave(draft.map((i) => i.id))} disabled={saving} className="gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save order'}
          </Button>
        </div>
      }
    >
      <p className="mb-3 text-xs text-muted-foreground">
        The number is the position shown on the POS — drag a row (or use the arrows) to reorder. Changes reach devices
        on the next sync.
      </p>
      {draft.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Nothing to reorder.</p>
      ) : (
        <ul className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {draft.map((item, i) => (
            <li
              key={item.id}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragIndex !== null && dragIndex !== i) {
                  reorder(dragIndex, i);
                  setDragIndex(i);
                }
              }}
              onDragEnd={() => setDragIndex(null)}
              className={`flex cursor-grab items-center gap-3 rounded-lg border px-3 py-2 active:cursor-grabbing ${
                dragIndex === i ? 'border-primary bg-muted/40' : 'border-border'
              }`}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {i + 1}
              </span>
              <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{item.name}</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={i === draft.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
